import { Button, Card, CardContent, cn } from "@exhousing/design-system";
import { AlertTriangle, Inbox, Settings } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import { EmptyState } from "@/components/EmptyState";
import { StatusBadge } from "@/components/StatusBadge";
import { useInboxMessages, useMailboxConnection } from "@/hooks/api/useInbox";
import { useState } from "react";

const FILTERS = [
  { key: "", label: "All" },
  { key: "unmatched", label: "Unmatched" },
  { key: "multiple", label: "Needs review" },
  { key: "matched", label: "Matched" },
] as const;

export function InboxPage() {
  const navigate = useNavigate();
  const { data: connection, isLoading: loadingConn } = useMailboxConnection();
  const [filter, setFilter] = useState<string>("");
  const { data: messages, isLoading } = useInboxMessages(filter || undefined);

  if (loadingConn) return <p className="text-sm text-muted-foreground">Loading…</p>;

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-medium tracking-tight">Inbox</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Correspondence from your connected mailbox, filed against the right person.
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate("/inbox/settings")}>
          <Settings className="size-4" />
          Mailbox settings
        </Button>
      </header>

      {!connection ? (
        <EmptyState
          icon={Inbox}
          title="Connect a mailbox"
          description="Link a mailbox from your own provider (Gmail, Outlook, your host). Balatin reads it read-only and files each message against the matching contact, lead, or contract."
          action={{
            label: "Connect a mailbox",
            onClick: () => navigate("/inbox/settings"),
          }}
        />
      ) : (
        <>
          {connection.needs_attention && (
            <Link
              to="/inbox/settings"
              className="flex items-start gap-3 rounded-lg border border-danger/30 bg-danger-soft/40 px-4 py-3 transition-colors hover:bg-danger-soft/60"
            >
              <AlertTriangle className="mt-0.5 size-5 shrink-0 text-danger" />
              <div className="text-sm">
                <p className="font-medium text-foreground">
                  Mailbox needs attention
                </p>
                <p className="text-muted-foreground">
                  {connection.last_error ??
                    "Syncing is paused. Open settings to check the connection."}
                </p>
              </div>
            </Link>
          )}

          {/* Triage filters */}
          <div className="flex flex-wrap gap-1.5">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => setFilter(f.key)}
                className={cn(
                  "rounded-full border px-3 py-1 text-sm transition-colors",
                  filter === f.key
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
              >
                {f.label}
              </button>
            ))}
          </div>

          <Card>
            <CardContent className="p-0">
              {isLoading ? (
                <p className="px-6 py-16 text-center text-sm text-muted-foreground">
                  Loading…
                </p>
              ) : messages && messages.length > 0 ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                      <th className="px-6 py-3 font-medium">From</th>
                      <th className="px-6 py-3 font-medium">Subject</th>
                      <th className="px-6 py-3 font-medium">Received</th>
                      <th className="px-6 py-3 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {messages.map((m) => (
                      <tr
                        key={m.id}
                        className="cursor-pointer border-b border-border/60 last:border-0 hover:bg-muted/40"
                        onClick={() => navigate(`/inbox/${m.id}`)}
                      >
                        <td className="px-6 py-3">
                          <span className="font-medium">
                            {m.from_name || m.from_addr || "—"}
                          </span>
                          {m.from_name && (
                            <span className="block text-xs text-muted-foreground">
                              {m.from_addr}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-3">
                          <span className="line-clamp-1">
                            {m.subject || "(no subject)"}
                          </span>
                          {m.snippet && (
                            <span className="line-clamp-1 text-xs text-muted-foreground">
                              {m.snippet}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-3 text-xs text-muted-foreground">
                          {m.sent_at
                            ? new Date(m.sent_at).toLocaleDateString("nl-NL")
                            : "—"}
                        </td>
                        <td className="px-6 py-3">
                          <StatusBadge status={m.match_status} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="flex flex-col items-center gap-3 py-16 text-center text-muted-foreground">
                  <Inbox className="size-8 opacity-40" />
                  <p className="text-sm">
                    {filter
                      ? "Nothing in this view."
                      : "No messages yet. New mail appears here within a few minutes."}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
